import { useState, useEffect, CSSProperties, Fragment } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn, Card, SectionTitle } from "../primitives";

/* ═══════════════════ EXTRACTION PROFILES ═══════════════════ */
export const KNOWN_FIELDS = ['Renewal Clause','Notice Period','Payment Terms','Limitation of Liability Summary','Indemnification Summary','Confidentiality Term','Assignment Clause','Termination Rights','Insurance Requirements','Dispute Resolution','License Scope','License Restrictions','IP Ownership','Service Level Requirements','Reference to Parent Agreement','Scope of Services','Deliverables','Fee Structure','Payment Schedule','Project Timeline','Milestones','Acceptance Criteria','Key Personnel','Sections Modified','Revised Terms','Amendment Effective Date','Restates Entire Agreement','Definition of Confidential Information','Permitted Disclosures','Return or Destruction of Information','Mutual or One-Way','Data Categories','Processing Purposes','Data Subject Rights','Sub-processor Requirements','Data Breach Notification Period','Data Transfer Mechanisms','Data Retention Period'];

export const DraftUploadRulesManager = () => {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [contractTypes, setContractTypes] = useState<string[]>([]);
  const [recipientTypeDefs, setRecipientTypeDefs] = useState<any[]>([]);
  const [newRule, setNewRule] = useState<Record<string, any>>({
    draft_label: 'internal_draft',
    target_status: '',
    routing_aware: false,
    routing_alt_status: '',
    authorized_roles: [] as string[],
    notify_on_transition: true,
    contract_type: '',
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Record<string, any>>({});
  const [notifs, setNotifs] = useState<any[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [newNotif, setNewNotif] = useState<Record<string, any>>({
    recipient_type: 'requester',
    recipient_value: '',
    channel: 'both',
    include_attachment: false,
    subject_template: '',
    lead_template: '',
    body_html_template: '',
    footer_html_template: '',
    cta_label_template: '',
    in_app_message_template: '',
    display_order: 100,
  });

  const LABELS = [
    { value: 'internal_draft', label: 'Internal Draft' },
    { value: 'company_draft', label: 'Company Draft' },
    { value: 'counterparty_draft', label: 'Counterparty Draft' },
    { value: 'final_draft', label: 'Final Draft' },
    { value: 'current_draft', label: 'Current Draft' },
    { value: 'execution_copy', label: 'Execution Copy' },
  ];

  const csvToArr = (s: string) => (s || '').split(',').map((r: string) => r.trim()).filter(Boolean);

  const load = async () => {
    setLoading(true);
    const [rulesRes, statusRes, rolesRes, typesRes, recipientTypesRes] = await Promise.all([
      apiCall('/settings/draft-upload-rules'),
      apiCall('/settings/picklists/request-statuses'),
      apiCall('/settings/picklists/roles'),
      apiCall('/settings/picklists/contract-types'),
      apiCall('/settings/picklists/recipient-types'),
    ]);
    if (rulesRes?.rules) setRules(rulesRes.rules);
    if (statusRes?.statuses) setStatuses(statusRes.statuses);
    if (rolesRes?.roles) setRoles(rolesRes.roles);
    if (typesRes?.types) setContractTypes(typesRes.types);
    if (recipientTypesRes?.types) setRecipientTypeDefs(recipientTypesRes.types);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newRule.draft_label || !newRule.target_status) {
      alert('draft_label and target_status are required');
      return;
    }
    if (newRule.routing_aware && !newRule.routing_alt_status) {
      alert('routing_alt_status is required when routing_aware is true');
      return;
    }
    const rolesArr = Array.isArray(newRule.authorized_roles) ? newRule.authorized_roles : csvToArr(newRule.authorized_roles);
    const body = {
      ...newRule,
      routing_alt_status: newRule.routing_aware ? newRule.routing_alt_status : null,
      authorized_roles: rolesArr.length > 0 ? rolesArr.join(',') : null,
      contract_type: newRule.contract_type || null,
    };
    const r = await apiCall('/settings/draft-upload-rules', { method: 'POST', body: JSON.stringify(body) });
    if (r?.rule) {
      setNewRule({ draft_label: 'internal_draft', target_status: '', routing_aware: false, routing_alt_status: '', authorized_roles: [], notify_on_transition: true, contract_type: '' });
      load();
    } else {
      alert(r?.error || 'Failed to add rule');
    }
  };

  const loadNotifs = async (ruleId: string) => {
    setNotifLoading(true);
    const r = await apiCall(`/settings/draft-upload-rules/${ruleId}/notifications`);
    setNotifs(r?.notifications || []);
    setNotifLoading(false);
  };

  const startEdit = async (rule: any) => {
    setEditingId(rule.id);
    setEditDraft({
      target_status: rule.target_status,
      routing_aware: rule.routing_aware,
      routing_alt_status: rule.routing_alt_status || '',
      authorized_roles: csvToArr(rule.authorized_roles || ''),
      notify_on_transition: rule.notify_on_transition,
      contract_type: rule.contract_type || '',
    });
    await loadNotifs(rule.id);
  };

  const saveEdit = async (id: string) => {
    const rolesArr = Array.isArray(editDraft.authorized_roles) ? editDraft.authorized_roles : csvToArr(editDraft.authorized_roles);
    const body = {
      ...editDraft,
      routing_alt_status: editDraft.routing_aware ? editDraft.routing_alt_status : null,
      authorized_roles: rolesArr.length > 0 ? rolesArr.join(',') : null,
      contract_type: editDraft.contract_type || null,
    };
    const r = await apiCall(`/settings/draft-upload-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    if (r?.rule) {
      setEditingId(null);
      setNotifs([]);
      load();
    } else {
      alert(r?.error || 'Failed to save');
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this draft upload rule? (Soft delete — set is_active = false)')) return;
    await apiCall(`/settings/draft-upload-rules/${id}`, { method: 'DELETE' });
    load();
  };

  const addNotif = async (ruleId: string) => {
    if (!newNotif.recipient_type) { alert('recipient_type required'); return; }
    const needsValue = ['role', 'user_id', 'email', 'field_lookup'].includes(newNotif.recipient_type);
    if (needsValue && !newNotif.recipient_value) { alert('recipient_value required for this recipient type'); return; }
    const body = { ...newNotif, recipient_value: needsValue ? newNotif.recipient_value : null };
    const r = await apiCall(`/settings/draft-upload-rules/${ruleId}/notifications`, { method: 'POST', body: JSON.stringify(body) });
    if (r?.notification) {
      await loadNotifs(ruleId);
      setNewNotif({
        recipient_type: 'requester', recipient_value: '', channel: 'both', include_attachment: false,
        subject_template: '', lead_template: '', body_html_template: '', footer_html_template: '',
        cta_label_template: '', in_app_message_template: '', display_order: 100,
      });
    } else {
      alert(r?.error || 'Failed to add notification');
    }
  };

  const removeNotif = async (ruleId: string, notifId: string) => {
    if (!window.confirm('Remove this notification row? (Soft delete)')) return;
    await apiCall(`/settings/draft-upload-rules/${ruleId}/notifications/${notifId}`, { method: 'DELETE' });
    await loadNotifs(ruleId);
  };

  const updateNotifField = async (ruleId: string, notifId: string, patch: Record<string, any>) => {
    const r = await apiCall(`/settings/draft-upload-rules/${ruleId}/notifications/${notifId}`, { method: 'PUT', body: JSON.stringify(patch) });
    if (r?.notification) {
      await loadNotifs(ruleId);
    } else {
      alert(r?.error || 'Failed to update notification');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 20, color: T.txM, fontSize: 12 }}>Loading draft upload rules...</div>;

  const inputStyle: CSSProperties = { padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' };
  const textareaStyle: CSSProperties = { ...inputStyle, fontFamily: 'monospace', resize: 'vertical' };
  const labelText = (val: string) => LABELS.find(l => l.value === val)?.label || val;

  const rolesCheckboxGroup = (selected: string[], onChange: (arr: string[]) => void) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '4px 0' }}>
      {roles.length === 0 && <span style={{ fontSize: 11, color: T.txM }}>(no roles loaded)</span>}
      {roles.map(role => (
        <label key={role} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.tx }}>
          <input
            type="checkbox"
            checked={selected.includes(role)}
            onChange={e => {
              const next = e.target.checked ? [...selected, role] : selected.filter(r => r !== role);
              onChange(next);
            }}
          />
          <span>{role}</span>
        </label>
      ))}
    </div>
  );

  const recipientValueInput = (recType: string, value: string, onChange: (v: string) => void) => {
    const def = recipientTypeDefs.find((d: any) => d.value === recType);
    if (!def || !def.needs_value) return <span style={{ fontSize: 11, color: T.txM }}>(not applicable)</span>;
    if (def.value_source === 'roles') {
      return (
        <select value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle as any}>
          <option value="">(select role)</option>
          {roles.map(rv => <option key={rv} value={rv}>{rv}</option>)}
        </select>
      );
    }
    const placeholder = def.value_source === 'users' ? 'user UUID'
      : def.value_source === 'free_text' ? 'email address'
      : 'request field name (e.g. business_owner_email)';
    return <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />;
  };

  const variablesHelp = (
    <div style={{ fontSize: 10, color: T.txM, marginTop: 2 }}>
      Variables: <code>{'{{counterparty}}'}</code> <code>{'{{agreement_type}}'}</code> <code>{'{{title}}'}</code> <code>{'{{request_id}}'}</code> <code>{'{{target_status}}'}</code> <code>{'{{uploader_name}}'}</code> <code>{'{{draft_ref}}'}</code>
    </div>
  );

  return (
    <div>
      <SectionTitle sub="Configure how draft uploads transition request status and which notifications fire per rule. Supports contract-type scoping, picklist-driven inputs, and per-rule notification templates. Per-tenant scoping is supported in the schema; UI today edits the global default only.">Draft Upload Rules</SectionTitle>

      <Card style={{ marginBottom: 12, padding: 12, backgroundColor: T.bg2 }}>
        <div style={{ fontSize: 11, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700, marginBottom: 8 }}>New Draft Upload Rule</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          <div>
            <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Draft Label *</div>
            <select value={newRule.draft_label} onChange={e => setNewRule((p: any) => ({ ...p, draft_label: e.target.value }))} style={inputStyle as any}>
              {LABELS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Contract Type</div>
            <select value={newRule.contract_type} onChange={e => setNewRule((p: any) => ({ ...p, contract_type: e.target.value }))} style={inputStyle as any}>
              <option value="">(Global default)</option>
              {contractTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Target Status *</div>
            <select value={newRule.target_status} onChange={e => setNewRule((p: any) => ({ ...p, target_status: e.target.value }))} style={inputStyle as any}>
              <option value="">(select status)</option>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Routing-Aware</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.tx, paddingTop: 6 }}>
              <input type="checkbox" checked={newRule.routing_aware} onChange={e => setNewRule((p: any) => ({ ...p, routing_aware: e.target.checked }))} />
              <span>If primary_route is commercial_ops, use Alt Status.</span>
            </label>
          </div>
          <div>
            <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Routing Alt Status</div>
            <select value={newRule.routing_alt_status} onChange={e => setNewRule((p: any) => ({ ...p, routing_alt_status: e.target.value }))} disabled={!newRule.routing_aware} style={{ ...inputStyle, opacity: newRule.routing_aware ? 1 : 0.5 } as any}>
              <option value="">(select alt status)</option>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Authorized Roles (blank = any)</div>
            {rolesCheckboxGroup(
              Array.isArray(newRule.authorized_roles) ? newRule.authorized_roles : csvToArr(newRule.authorized_roles),
              arr => setNewRule((p: any) => ({ ...p, authorized_roles: arr }))
            )}
          </div>
          <div style={{ gridColumn: 'span 1', display: 'flex', alignItems: 'center', paddingTop: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.tx }}>
              <input type="checkbox" checked={newRule.notify_on_transition} onChange={e => setNewRule((p: any) => ({ ...p, notify_on_transition: e.target.checked }))} />
              <span>Notify on transition</span>
            </label>
          </div>
          <div style={{ gridColumn: 'span 1', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
            <Btn v="primary" sz="sm" onClick={add}>Add Rule</Btn>
          </div>
        </div>
      </Card>

      <Card style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${T.brd}` }}>
              {['Label', 'Contract Type', 'Target Status', 'Routing-Aware', 'Alt Status', 'Authorized Roles', 'Notify', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rules.map((r: any) => {
              if (editingId === r.id) {
                return (
                  <Fragment key={r.id}>
                    <tr style={{ borderBottom: `1px solid ${T.brd}`, backgroundColor: T.bg2 }}>
                      <td style={{ padding: '8px 10px', color: T.tx }}>{labelText(r.draft_label)}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <select value={editDraft.contract_type} onChange={e => setEditDraft((p: any) => ({ ...p, contract_type: e.target.value }))} style={inputStyle as any}>
                          <option value="">(Global default)</option>
                          {contractTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <select value={editDraft.target_status} onChange={e => setEditDraft((p: any) => ({ ...p, target_status: e.target.value }))} style={inputStyle as any}>
                          <option value="">(select status)</option>
                          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '8px 10px' }}><input type="checkbox" checked={editDraft.routing_aware} onChange={e => setEditDraft((p: any) => ({ ...p, routing_aware: e.target.checked }))} /></td>
                      <td style={{ padding: '8px 10px' }}>
                        <select value={editDraft.routing_alt_status} onChange={e => setEditDraft((p: any) => ({ ...p, routing_alt_status: e.target.value }))} disabled={!editDraft.routing_aware} style={{ ...inputStyle, opacity: editDraft.routing_aware ? 1 : 0.5 } as any}>
                          <option value="">(select alt status)</option>
                          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '8px 10px', minWidth: 200 }}>
                        {rolesCheckboxGroup(
                          Array.isArray(editDraft.authorized_roles) ? editDraft.authorized_roles : csvToArr(editDraft.authorized_roles),
                          arr => setEditDraft((p: any) => ({ ...p, authorized_roles: arr }))
                        )}
                      </td>
                      <td style={{ padding: '8px 10px' }}><input type="checkbox" checked={editDraft.notify_on_transition} onChange={e => setEditDraft((p: any) => ({ ...p, notify_on_transition: e.target.checked }))} /></td>
                      <td style={{ padding: '8px 10px', display: 'flex', gap: 6 }}>
                        <Btn v="primary" sz="sm" onClick={() => saveEdit(r.id)}>Save</Btn>
                        <Btn v="secondary" sz="sm" onClick={() => { setEditingId(null); setNotifs([]); }}>Cancel</Btn>
                      </td>
                    </tr>
                    <tr style={{ borderBottom: `1px solid ${T.brd}`, backgroundColor: T.bg2 }}>
                      <td colSpan={8} style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 11, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700, marginBottom: 8 }}>Notifications for this rule</div>
                        {notifLoading && <div style={{ fontSize: 11, color: T.txM }}>Loading…</div>}
                        {!notifLoading && notifs.length === 0 && (
                          <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>No notifications configured. Hardcoded pre-Tier-2 fallback will fire for this rule.</div>
                        )}
                        {!notifLoading && notifs.map((n: any) => (
                          <div key={n.id} style={{ border: `1px solid ${T.brd}`, borderRadius: 4, padding: 10, marginBottom: 8, backgroundColor: T.bg3 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 8 }}>
                              <div>
                                <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Recipient Type</div>
                                <select value={n.recipient_type} onChange={e => updateNotifField(r.id, n.id, { recipient_type: e.target.value, recipient_value: null })} style={inputStyle as any}>
                                  {recipientTypeDefs.map((d: any) => <option key={d.value} value={d.value}>{d.label}</option>)}
                                </select>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Recipient Value</div>
                                {recipientValueInput(n.recipient_type, n.recipient_value, v => updateNotifField(r.id, n.id, { recipient_value: v }))}
                              </div>
                              <div>
                                <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Channel</div>
                                <select value={n.channel} onChange={e => updateNotifField(r.id, n.id, { channel: e.target.value })} style={inputStyle as any}>
                                  <option value="in_app">In-app</option>
                                  <option value="email">Email</option>
                                  <option value="both">Both</option>
                                </select>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Attachment</div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.tx, paddingTop: 6 }}>
                                  <input type="checkbox" checked={!!n.include_attachment} onChange={e => updateNotifField(r.id, n.id, { include_attachment: e.target.checked })} />
                                  <span>Include latest draft</span>
                                </label>
                              </div>
                            </div>
                            <div style={{ marginBottom: 6 }}>
                              <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Subject</div>
                              <input value={n.subject_template || ''} onChange={e => updateNotifField(r.id, n.id, { subject_template: e.target.value })} style={inputStyle} />
                            </div>
                            <div style={{ marginBottom: 6 }}>
                              <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Lead</div>
                              <textarea value={n.lead_template || ''} onChange={e => updateNotifField(r.id, n.id, { lead_template: e.target.value })} rows={2} style={textareaStyle as any} />
                            </div>
                            <div style={{ marginBottom: 6 }}>
                              <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Body HTML</div>
                              <textarea value={n.body_html_template || ''} onChange={e => updateNotifField(r.id, n.id, { body_html_template: e.target.value })} rows={5} style={textareaStyle as any} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                              <div>
                                <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Footer HTML</div>
                                <textarea value={n.footer_html_template || ''} onChange={e => updateNotifField(r.id, n.id, { footer_html_template: e.target.value })} rows={2} style={textareaStyle as any} />
                              </div>
                              <div>
                                <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>CTA Label</div>
                                <input value={n.cta_label_template || ''} onChange={e => updateNotifField(r.id, n.id, { cta_label_template: e.target.value })} placeholder="View draft" style={inputStyle} />
                              </div>
                            </div>
                            <div style={{ marginBottom: 6 }}>
                              <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>In-App Message</div>
                              <input value={n.in_app_message_template || ''} onChange={e => updateNotifField(r.id, n.id, { in_app_message_template: e.target.value })} style={inputStyle} />
                            </div>
                            {variablesHelp}
                            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                              <Btn v="secondary" sz="sm" onClick={() => removeNotif(r.id, n.id)}>Remove</Btn>
                            </div>
                          </div>
                        ))}
                        <div style={{ borderTop: `1px solid ${T.brd}`, paddingTop: 10, marginTop: 8 }}>
                          <div style={{ fontSize: 11, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700, marginBottom: 6 }}>Add notification</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 6 }}>
                            <div>
                              <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Recipient Type</div>
                              <select value={newNotif.recipient_type || 'requester'} onChange={e => setNewNotif((p: any) => ({ ...p, recipient_type: e.target.value, recipient_value: '' }))} style={inputStyle as any}>
                                {recipientTypeDefs.map((d: any) => <option key={d.value} value={d.value}>{d.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Recipient Value</div>
                              {recipientValueInput(newNotif.recipient_type || 'requester', newNotif.recipient_value || '', v => setNewNotif((p: any) => ({ ...p, recipient_value: v })))}
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Channel</div>
                              <select value={newNotif.channel || 'both'} onChange={e => setNewNotif((p: any) => ({ ...p, channel: e.target.value }))} style={inputStyle as any}>
                                <option value="in_app">In-app</option>
                                <option value="email">Email</option>
                                <option value="both">Both</option>
                              </select>
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Attachment</div>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.tx, paddingTop: 6 }}>
                                <input type="checkbox" checked={!!newNotif.include_attachment} onChange={e => setNewNotif((p: any) => ({ ...p, include_attachment: e.target.checked }))} />
                                <span>Include latest draft</span>
                              </label>
                            </div>
                          </div>
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Subject</div>
                            <input value={newNotif.subject_template || ''} onChange={e => setNewNotif((p: any) => ({ ...p, subject_template: e.target.value }))} style={inputStyle} />
                          </div>
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Lead</div>
                            <textarea value={newNotif.lead_template || ''} onChange={e => setNewNotif((p: any) => ({ ...p, lead_template: e.target.value }))} rows={2} style={textareaStyle as any} />
                          </div>
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Body HTML</div>
                            <textarea value={newNotif.body_html_template || ''} onChange={e => setNewNotif((p: any) => ({ ...p, body_html_template: e.target.value }))} rows={5} style={textareaStyle as any} />
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                            <div>
                              <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Footer HTML</div>
                              <textarea value={newNotif.footer_html_template || ''} onChange={e => setNewNotif((p: any) => ({ ...p, footer_html_template: e.target.value }))} rows={2} style={textareaStyle as any} />
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>CTA Label</div>
                              <input value={newNotif.cta_label_template || ''} onChange={e => setNewNotif((p: any) => ({ ...p, cta_label_template: e.target.value }))} placeholder="View draft" style={inputStyle} />
                            </div>
                          </div>
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>In-App Message</div>
                            <input value={newNotif.in_app_message_template || ''} onChange={e => setNewNotif((p: any) => ({ ...p, in_app_message_template: e.target.value }))} style={inputStyle} />
                          </div>
                          {variablesHelp}
                          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                            <Btn v="primary" sz="sm" onClick={() => addNotif(r.id)}>Add Notification</Btn>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                );
              }
              return (
                <tr key={r.id} style={{ borderBottom: `1px solid ${T.brd}` }}>
                  <td style={{ padding: '8px 10px', color: T.tx, fontWeight: 600 }}>{labelText(r.draft_label)}</td>
                  <td style={{ padding: '8px 10px', color: T.tx, fontFamily: 'monospace', fontSize: 11 }}>{r.contract_type || '(global)'}</td>
                  <td style={{ padding: '8px 10px', color: T.tx, fontFamily: 'monospace' }}>{r.target_status}</td>
                  <td style={{ padding: '8px 10px', color: T.tx }}>{r.routing_aware ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '8px 10px', color: T.tx, fontFamily: 'monospace' }}>{r.routing_alt_status || '\u2014'}</td>
                  <td style={{ padding: '8px 10px', color: T.tx, fontFamily: 'monospace', fontSize: 11 }}>{r.authorized_roles || '(any)'}</td>
                  <td style={{ padding: '8px 10px', color: T.tx }}>{r.notify_on_transition ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '8px 10px', display: 'flex', gap: 6 }}>
                    <Btn v="secondary" sz="sm" onClick={() => startEdit(r)}>Edit</Btn>
                    <Btn v="secondary" sz="sm" onClick={() => remove(r.id)}>Delete</Btn>
                  </td>
                </tr>
              );
            })}
            {rules.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: T.txM, fontSize: 12 }}>No active rules. Each draft label will fall back to its hardcoded default.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
